import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Award, FileCheck2, ScanLine, Building2 } from "lucide-react";
import { KarneModal } from "@/pages/OptikSinav";
import { KurumKarneModal, KurumBulkKarneModal } from "@/pages/KurumSinav";

export default function KarnePaneli() {
  const [type, setType] = useState("optik"); // optik | kurum
  const [exams, setExams] = useState([]);
  const [kurums, setKurums] = useState([]);
  const [selId, setSelId] = useState("");
  const [week, setWeek] = useState(1);
  const [rows, setRows] = useState([]);
  const [karne, setKarne] = useState(null); // { kind, data }
  const [bulkKarne, setBulkKarne] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  const openBulkKarne = async () => {
    if (type !== "kurum" || !rows.length) return;
    setBulkLoading(true);
    try {
      const items = await Promise.all(rows.map((r) => api.get(`/kurum-sinavlar/${selId}/week/${week}/karne`, { params: { student_id: r.student_id } }).then(({ data }) => data)));
      setBulkKarne({ title: `${selKurum?.name} — ${week}. Hafta`, items });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBulkLoading(false);
  };

  useEffect(() => {
    api.get("/optik-exams").then(({ data }) => setExams(data)).catch(() => {});
    api.get("/kurum-sinavlar").then(({ data }) => setKurums(data)).catch(() => {});
  }, []);

  const selKurum = kurums.find((k) => k.id === selId);
  const selExam = exams.find((e) => e.id === selId);

  useEffect(() => {
    setRows([]);
    if (!selId) return;
    const url = type === "optik" ? `/optik-exams/${selId}/evaluation` : `/kurum-sinavlar/${selId}/week/${week}/evaluation`;
    api.get(url).then(({ data }) => setRows(data.rows || [])).catch(() => setRows([]));
  }, [type, selId, week]);

  const openKarne = async (sid) => {
    try {
      const { data } = type === "optik"
        ? await api.get(`/optik-exams/${selId}/karne`, { params: { student_id: sid } })
        : await api.get(`/kurum-sinavlar/${selId}/week/${week}/karne`, { params: { student_id: sid } });
      setKarne({ kind: type, data });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sel = "rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100";

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-600"><Award className="h-6 w-6" /></div>
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Karne Paneli</h1>
          <p className="text-slate-500">Sınav veya kurum serisi seçin — sınıf listesinden öğrenci karnesini açıp yazdırabilirsiniz.</p>
        </div>
      </div>

      <div className="flex gap-2">
        {[["optik", "Optik Sınavlar", ScanLine], ["kurum", "Kurum Sınavları", Building2]].map(([k, l, Icon]) => (
          <button key={k} data-testid={`karne-type-${k}`} onClick={() => { setType(k); setSelId(""); setWeek(1); }} className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${type === k ? "bg-[#1E293B] text-white" : "border border-slate-200 bg-white text-slate-600 hover:border-indigo-300"}`}><Icon className="h-4 w-4" /> {l}</button>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <select data-testid="karne-exam-select" value={selId} onChange={(e) => setSelId(e.target.value)} className={`${sel} w-full max-w-xl`}>
          <option value="">{type === "optik" ? "Sınav seçin…" : "Kurum serisi seçin…"}</option>
          {(type === "optik" ? exams : kurums).map((x) => (
            <option key={x.id} value={x.id}>{x.name}{x.date ? ` · ${x.date}` : ""} · {(x.grades?.length ? x.grades : [x.grade]).join(",")}. Sınıf</option>
          ))}
        </select>
        {type === "kurum" && selKurum && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {Array.from({ length: selKurum.week_count }).map((_, i) => (
              <button key={i} data-testid={`karne-week-${i + 1}`} onClick={() => setWeek(i + 1)} className={`h-8 w-11 rounded-lg text-xs font-bold transition ${week === i + 1 ? "bg-indigo-600 text-white shadow" : "border border-slate-200 bg-white text-slate-500 hover:border-indigo-300"}`}>{i + 1}</button>
            ))}
          </div>
        )}
      </div>

      {selId && (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center gap-2 bg-[#1E293B] px-5 py-3 text-white">
            <FileCheck2 className="h-5 w-5" />
            <span className="font-bold">{type === "optik" ? selExam?.name : `${selKurum?.name} — ${week}. Hafta`}</span>
            {type === "kurum" && rows.length > 0 && <button data-testid="karne-bulk-btn" onClick={openBulkKarne} disabled={bulkLoading} className="ml-auto flex items-center gap-1.5 rounded-lg bg-amber-500 px-3.5 py-1.5 text-xs font-bold text-white transition hover:bg-amber-600 active:scale-95 disabled:opacity-60">{bulkLoading ? "Hazırlanıyor…" : `Toplu Karne Al (${rows.length})`}</button>}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs font-bold text-slate-500"><tr><th className="px-4 py-2 text-left">#</th><th className="px-4 py-2 text-left">Öğrenci</th><th className="px-3 py-2">D</th><th className="px-3 py-2">Y</th><th className="px-3 py-2">B</th><th className="px-3 py-2">Net</th><th className="px-3 py-2">LGS Puanı</th><th className="px-3 py-2">Karne</th></tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={8} className="py-8 text-center text-slate-400">Bu seçimde sonuç yok — önce optik/toplu işaretleme yapın.</td></tr> : rows.map((r, i) => (
                <tr key={r.student_id} data-testid={`karne-row-${i}`} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-bold text-slate-400">{i + 1}</td>
                  <td className="px-4 py-2 font-semibold text-slate-700">{r.name}{r.section ? ` /${r.section}` : ""}</td>
                  <td className="px-3 py-2 text-center text-emerald-600">{r.total.dogru}</td>
                  <td className="px-3 py-2 text-center text-rose-600">{r.total.yanlis}</td>
                  <td className="px-3 py-2 text-center text-slate-500">{r.total.bos}</td>
                  <td className="px-3 py-2 text-center font-extrabold text-indigo-600">{r.total.net}</td>
                  <td data-testid={`karne-puan-${i}`} className="px-3 py-2 text-center font-bold text-[#16324F]">{r.puan ?? "—"}</td>
                  <td className="px-3 py-2 text-center"><button data-testid={`karne-open-${i}`} onClick={() => openKarne(r.student_id)} className="rounded-lg bg-indigo-600 px-4 py-1.5 text-xs font-bold text-white transition hover:bg-indigo-700 active:scale-95">Karne</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {karne?.kind === "optik" && <KarneModal karne={karne.data} onClose={() => setKarne(null)} isEditor onChanged={() => openKarne(karne.data.student.id)} />}
      {karne?.kind === "kurum" && <KurumKarneModal karne={karne.data} onClose={() => setKarne(null)} isEditor onChanged={() => openKarne(karne.data.student.id)} />}
      {bulkKarne && <KurumBulkKarneModal title={bulkKarne.title} items={bulkKarne.items} onClose={() => setBulkKarne(null)} />}
    </div>
  );
}
